import React, { useState, useEffect } from 'react';
import { useCategoryGroupsData, GroupPurposeType } from '../../../../hooks/useCategoryGroupsData'; // Adjust path if needed
import { listAllCategoriesForSelect } from '../../../../services/categoryGroupApis'; // Adjust path if needed
import CategoryGroupCard from '../../../../components/Admin/Category/CategoryGroup/CategoryGroupCard'; // Adjust path if needed
import CategoryGroupFormModal from '../../../../components/Admin/Category/CategoryGroup/CategoryGroupFormModal'; // Adjust path if needed
// ConfirmationModal is generally used within CategoryGroupCard, so not directly invoked here.
import { toProperCase } from '../../../../utility/textFormatting'; // Assuming this path is correct
import { useParams } from 'react-router-dom'; // Assuming

// Tailwind Styled Tabs Component
const Tabs = ({ activeTab, onTabClick, children }) => (
  <div>
    <div className="mb-6 border-b border-gray-300 flex space-x-1 sm:space-x-2">
      {React.Children.map(children, (child) => (
        <button
          key={child.props.label}
          onClick={() => onTabClick(child.props.label)}
          className={`px-3 sm:px-4 py-2.5 -mb-px border-b-2 font-medium text-sm focus:outline-none transition-colors duration-150 ease-in-out
            ${activeTab === child.props.label
              ? 'border-indigo-600 text-indigo-700'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-400'
            }`}
          aria-current={activeTab === child.props.label ? 'page' : undefined}
        >
          {child.props.label}
        </button>
      ))}
    </div>
    <div>
      {React.Children.map(children, child =>
        child.props.label === activeTab ? child.props.children : null
      )}
    </div>
  </div>
);

// Tab component (label prop is read by Tabs, not used by Tab itself for rendering)
const Tab = ({ children }) => <div>{children}</div>;


// Tailwind Styled Collapsible Section Component
const CollapsibleSection = ({ title, children, initiallyOpen = false }) => {
    const [isOpen, setIsOpen] = useState(initiallyOpen);
    return (
        <div className="mb-6 bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-4 py-3.5 bg-gray-50 hover:bg-gray-100 text-left font-semibold text-gray-700 cursor-pointer flex justify-between items-center focus:outline-none transition-colors duration-150 ease-in-out"
                aria-expanded={isOpen}
            >
                <span className="text-base">{title}</span>
                <svg 
                    className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ease-in-out ${isOpen ? 'rotate-180' : 'rotate-0'}`}
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>
            {isOpen && <div className="p-4 border-t border-gray-200">{children}</div>}
        </div>
    );
};


export default function CategoryGroupsPage() {
  const [activeTabLabel, setActiveTabLabel] = useState('Active Groups');
  const initialIsActiveForHook = activeTabLabel === 'Active Groups';
  const { locale: urlLocale } = useParams();  
  const { 
    groups, 
    loading, 
    error, 
    setCurrentView, 
    refreshGroups, 
    updateGroupInState 
  } = useCategoryGroupsData(initialIsActiveForHook, true, urlLocale);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  
  const [allChildCategories, setAllChildCategories] = useState([]);
  const [childCategoriesLoading, setChildCategoriesLoading] = useState(false);
  const isMasterLocale = urlLocale === 'master';
  
  console.log("Available groups:",groups)
  useEffect(() => {
    if (!isMasterLocale)
    {
      const fetchChildCats = async () => {
      setChildCategoriesLoading(true);
      try {
        const response = await listAllCategoriesForSelect({ type: 'child', limit: 1000, locale: urlLocale}); 
        setAllChildCategories(response.data.filter(cat => cat.type === 'child') || []); 
      } catch (err) {
        console.error("Failed to fetch child categories for select:", err);
        setAllChildCategories([]);
      } finally {
        setChildCategoriesLoading(false);
      }
      // console.log("Fetched child categories:", allChildCategories); 
    };
    fetchChildCats();}
  }, []);

  const handleTabClick = (tabLabel) => {
    setActiveTabLabel(tabLabel);
    setCurrentView(tabLabel === 'Active Groups' ? 'active' : 'inactive');
  };

  const handleOpenFormModal = (group = null) => {
    setEditingGroup(group);
    setIsFormModalOpen(true);
  };

  const handleFormSave = (savedGroupWithChildren) => {
    updateGroupInState(savedGroupWithChildren); 
    setIsFormModalOpen(false); 
    setEditingGroup(null);
  };

  const handleCloseFormModal = () => {
    setEditingGroup(null);
    setIsFormModalOpen(false);
    // refreshGroups(); // Optionally refresh data from API if optimistic update isn't sufficient
  };
  
  const groupedByCategoryPurpose = groups.reduce((acc, group) => {
    if (group.purposes && group.purposes.length > 0) {
      group.purposes.forEach(purpose => {
        if (!acc[purpose]) acc[purpose] = [];
        acc[purpose].push(group);
      });
    } else {
      if (!acc['Uncategorized']) acc['Uncategorized'] = [];
      acc['Uncategorized'].push(group);
    }
    return acc;
  }, {});

  const renderGroupSections = () => {
    if (loading) return <div className="text-center py-10"><p className="text-gray-500 text-lg">Loading groups...</p></div>;
    if (error) return <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md my-4" role="alert"><p className="font-bold">Error:</p><p>{error}</p></div>;
    if (!loading && groups.length === 0) return <div className="text-center py-10"><p className="text-gray-500 text-lg">No groups found for this view.</p></div>;

    const sections = Object.values(GroupPurposeType).map(purposeValue => {
      const groupsForPurpose = groupedByCategoryPurpose[purposeValue] || [];
      if (groupsForPurpose.length === 0) return null;
      return (
        <CollapsibleSection 
          key={purposeValue}
          title={`${toProperCase(purposeValue)} (${groupsForPurpose.length})`}
          initiallyOpen={true}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
            {groupsForPurpose.map(group => (
              <CategoryGroupCard
                key={group.id}
                group={group}
                onEdit={() => handleOpenFormModal(group)}
                onDeleteSuccess={refreshGroups}
                locale={urlLocale}
              />
            ))}
          </div>
        </CollapsibleSection>
      );
    }).filter(Boolean); // Remove null entries

    const uncategorizedGroups = groupedByCategoryPurpose['Uncategorized'] || [];
    if (uncategorizedGroups.length > 0) {
      sections.push(
         <CollapsibleSection 
            key="Uncategorized"
            title={`Uncategorized (${uncategorizedGroups.length})`} 
            initiallyOpen={true}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                {uncategorizedGroups.map(group => (
                    <CategoryGroupCard
                        key={group.id}
                        group={group}
                        onEdit={() => handleOpenFormModal(group)}
                        onDeleteSuccess={refreshGroups}
                        locale={urlLocale}
                    />
                ))}
            </div>
         </CollapsibleSection>
      );
    }
    
    if (sections.length === 0 && groups.length > 0) {
        // This case might happen if all groups are uncategorized and the above logic for uncategorized is not hit (should not happen with current logic)
        // Or if GroupPurposeType is empty / all groups fall into Uncategorized but it's not the last one.
        // For safety, if sections are empty but groups exist, render them flat (though current logic should cover uncategorized correctly)
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 mt-4">
                {groups.map(group => (
                     <CategoryGroupCard
                        key={group.id}
                        group={group}
                        onEdit={() => handleOpenFormModal(group)}
                        onDeleteSuccess={refreshGroups}
                        locale={urlLocale}
                    />
                ))}
            </div>
        );
    }


    return sections.length > 0 ? <>{sections}</> : <div className="text-center py-10"><p className="text-gray-500 text-lg">No groups to display in sections.</p></div>;
  };


  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {isMasterLocale ? (
        // --- Content for Master Locale ---
        <>
          <header className="flex flex-col sm:flex-row justify-between items-center mb-6 pb-4 border-b border-gray-300">
             <h1 className="text-2xl font-semibold text-gray-800 mb-3 sm:mb-0">Manage Category Groups</h1>
          </header>
          <div className="p-4 border rounded bg-card text-card-foreground">
              <p>Category Groups are specific to locales and cannot be managed at the Master level.</p>
              <p className="mt-2 text-sm text-muted-foreground">Please select a specific country locale to view or manage groups.</p>
          </div>
        </>
      ) : (
        // --- Original Content for Localized Locales ---
        <>
          <header className="flex flex-col sm:flex-row justify-between items-center mb-6 pb-4 border-b border-gray-300">
            <h1 className="text-2xl font-semibold text-gray-800 mb-3 sm:mb-0">Manage Category Groups ({urlLocale.toUpperCase()})</h1> {/* Show locale */}
            <button
                onClick={() => handleOpenFormModal()}
                className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center transition-colors duration-150 ease-in-out"
            >
               {/* ... (Add New Group button content) ... */}
                Add New Group
            </button>
          </header>

          <Tabs activeTab={activeTabLabel} onTabClick={handleTabClick}>
            <Tab label="Active Groups">
              {renderGroupSections()}
            </Tab>
            <Tab label="Inactive Groups">
              {renderGroupSections()}
            </Tab>
          </Tabs>

          {isFormModalOpen && (
            <CategoryGroupFormModal
              isOpen={isFormModalOpen}
              onClose={handleCloseFormModal}
              group={editingGroup}
              allPurposes={Object.values(GroupPurposeType)}
              allChildCategories={allChildCategories}
              childCategoriesLoading={childCategoriesLoading}
              onGroupSaved={handleFormSave}
              locale={urlLocale} // Pass the current urlLocale
            />
          )}
        </>
      )}
    </div>
  );
}